import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { actionForm } from '../../../../interfaces'
import { notDanger, notSuccess } from '../../../../utils/notification'
import { swalException } from '../../../../utils/swal'
import { apiMonedaListado } from '../../api/apiMonedaListado'
import { apiMonedaRegistro } from '../../api/apiMonedaRegistro'
import { monedaInputValidator } from '../../validator/monedaInputValidator'
import MonedaForm from './MonedaForm'
import {
  MONEDA_DEFAULT,
  MonedaApiInputProps,
  MonedaInputProps,
  MonedaProps,
} from '../../../../interfaces/monedaPrecio.ts'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: MonedaProps) => void
}

type Props = OwnProps

/**
 * Formulario de upsert de moneda primaria
 * @param props
 * @constructor
 */
const MonedaPrimariaRegistroDialog: FunctionComponent<Props> = (props) => {
  const { id, keepMounted, open, onClose, ...other } = props

  const [formLoading, setFormLoading] = useState<boolean>(false)

  const form = useForm<MonedaInputProps>({
    defaultValues: { ...MONEDA_DEFAULT, tipoCambio: 1, tipoCambioCompra: 1 },
    resolver: yupResolver(monedaInputValidator),
  })

  const confirm = useConfirm()

  const { control, setValue } = form

  const sinMoneda = useWatch({ control, name: 'sinMoneda' })

  const onSubmit = (data: MonedaInputProps) => {
    if (data.tipoCambio !== 1 || data.tipoCambioCompra !== 1) {
      notDanger('Los tipos de cambio deben ser 1.00')
      return
    }
    const title = (
      <Typography variant={'subtitle1'} component={'span'}>
        Registro moneda Local
      </Typography>
    )
    const message = (
      <>
        <Typography variant={'body2'} gutterBottom>
          Confirma que desea registrar la moneda{' '}
          <strong>
            {data.sigla} - {data.descripcion}
          </strong>{' '}
        </Typography>
        <Typography variant={'body2'} color={'warning'} gutterBottom>
          Una vez registrada, ya no será posible eliminar ni modificar la moneda local.
        </Typography>
      </>
    )

    const monedaInput: MonedaApiInputProps = {
      codigoSinMoneda: data.sinMoneda!.codigoClasificador,
      sigla: data.sigla,
      descripcion: data.descripcion,
      tipoCambio: data.tipoCambio,
      tipoCambioCompra: data.tipoCambioCompra,
    }
    confirm({ title, content: message })
      .then(async () => {
        setFormLoading(true)
        // Verificamos si ya existe la moneda registrada
        const miMoneda = (await apiMonedaListado()) || []
        const moneda = miMoneda.find(
          (m) => m.codigo.toString() === monedaInput.codigoSinMoneda.toString(),
        )
        if (!moneda) {
          const resp = await apiMonedaRegistro(monedaInput).catch((e) => {
            swalException(e)
          })
          if (resp) {
            notSuccess('Moneda registrado correctamente')
            onClose(resp)
          }
        }
        onClose(moneda)
      })
      .catch(() => {
        setFormLoading(false)
      })
      .finally(() => {
        setFormLoading(false)
      })
  }

  const onError = (error: any) => console.log(error)

  useEffect(() => {
    if (sinMoneda) {
      setValue('descripcion', `${sinMoneda.descripcion}`)
    }
  }, [sinMoneda])

  return (
    <Dialog
      maxWidth="xs"
      id={id}
      keepMounted={keepMounted}
      open={open}
      sx={{ '& .MuiDialog-paper': { minWidth: '33vw' } }}
      {...other}
    >
      <DialogTitle>Registro Moneda Principal</DialogTitle>
      <DialogContent dividers>
        <MonedaForm form={form} action={actionForm.REGISTER} monedaPrimaria={null} />
      </DialogContent>
      <DialogActions>
        <Button
          color={'error'}
          disabled={formLoading}
          onClick={() => {
            onClose()
          }}
        >
          Cerrar
        </Button>
        <LoadingButton
          variant={'contained'}
          size={'small'}
          color={'primary'}
          loading={formLoading}
          sx={{ mr: 2 }}
          onClick={() => form.handleSubmit(onSubmit, onError)()}
        >
          Registrar Moneda Principal
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default MonedaPrimariaRegistroDialog
